import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Info, Pencil, Phone, Plus, AlertCircle, Trash2 } from 'lucide-react'
import { api, type Me } from '../lib/api'
import {
  ARCH_ZH,
  CASE_STATUS_ZH,
  CASE_TYPE_ZH,
  LANG_ZH,
  date as fmtDate,
  relativeDay,
  rm,
} from '../lib/format'
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Modal,
  Skeleton,
  caseStatusTone,
  useToast,
} from '../components/ui'
import { NewCaseDrawer } from '../features/NewCaseDrawer'
import { CustomerDrawer } from '../features/CustomerDrawer'
import { useMutation, useQueryClient } from '@tanstack/react-query'

interface CustomerDetail {
  id: string
  code: string
  fullName: string
  phoneE164: string
  phoneMasked: string
  altPhoneE164: string | null
  altPhoneIsFamily: boolean
  gender: string | null
  approxAge: number | null
  language: string
  addressLine: string | null
  area: string | null
  referredBy: { id: string; fullName: string } | null
  referrals: Array<{ id: string; fullName: string }>
  healthNotes: string | null
  notes: string | null
  optOut: boolean
  lifetimeAmount: number | null
  outstandingAmount: number | null
  hasOutstanding: boolean
  caseCount: number
  cases: Array<{
    id: string
    caseNo: string
    type: string
    typeLabel: string
    arch: string | null
    status: string
    finalAmount: number
    balanceAmount: number
    promisedDate: string | null
    deliveredAt: string | null
    daysSinceDelivered: number | null
    dueForRenewal: boolean
    currentStep: string | null
  }>
}

export function CustomerDetailPage({ me }: { me: Me }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const toast = useToast()
  const [newCaseOpen, setNewCaseOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [delOpen, setDelOpen] = useState(false)
  const [delReason, setDelReason] = useState('')

  const q = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get<{ customer: CustomerDetail }>(`/api/customers/${id}`),
  })

  const del = useMutation({
    mutationFn: () => api.del(`/api/customers/${id}`, { reason: delReason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customer-list'] })
      void qc.invalidateQueries({ queryKey: ['search'] })
      toast.show('顾客已删除')
      navigate('/customers', { replace: true })
    },
    onError: (e) => toast.show((e as Error).message, { tone: 'bad' }),
  })

  if (q.isLoading)
    return (
      <div className="mx-auto max-w-[1120px] flex flex-col gap-4">
        <Skeleton className="h-9 w-40" />
        <Skeleton className="h-52" />
        <Skeleton className="h-72" />
      </div>
    )
  if (q.isError)
    return (
      <p className="py-12 text-center text-[17px] text-ink-2">
        {(q.error as Error).message}
      </p>
    )
  if (!q.data) return null

  const c = q.data.customer
  const isOwner = me.user.role === 'OWNER'
  // 后端会拦，但提前算一遍，好在确认框里直接说清楚为什么删不掉
  const blockingCases = c.cases.filter(
    (cs) => (cs.status !== 'CLOSED' && cs.status !== 'CANCELLED') || cs.balanceAmount > 0,
  ).length

  return (
    <div className="mx-auto max-w-[1120px]">
      <button
        onClick={() => navigate(-1)}
        className="mb-3 flex h-11 items-center gap-1.5 text-[17px] text-ink-2 hover:text-ink"
      >
        <ArrowLeft size={20} /> 顾客
      </button>

      <Card className="mb-4">
        <div className="flex flex-wrap items-start gap-4">
          <Avatar name={c.fullName} size={56} />
          <div className="min-w-0 flex-1">
            <h1 className="text-[28px] font-bold">{c.fullName}</h1>
            <p className="tnum text-[16px] text-ink-2">
              {c.code}
              {c.gender ? ` · ${c.gender === 'F' ? '女' : '男'}` : ''}
              {c.approxAge ? ` · 约 ${c.approxAge} 岁` : ''}
              {` · ${LANG_ZH[c.language] ?? c.language}`}
            </p>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
              <a
                href={`tel:${c.phoneE164}`}
                className="tnum text-[17px] font-medium text-brand-700 hover:underline"
              >
                {c.phoneE164}
              </a>
              <Button size="sm" icon={<Phone size={16} />} onClick={() => window.open(`tel:${c.phoneE164}`)}>
                拨打
              </Button>
              {c.altPhoneE164 && (
                <span className="tnum text-[16px] text-ink-3">
                  {c.altPhoneIsFamily ? '家属 ' : '备用 '}
                  {c.altPhoneE164}
                </span>
              )}
            </div>

            {(c.addressLine || c.area) && (
              <p className="mt-1 text-[16px] text-ink-2">
                {[c.addressLine, c.area].filter(Boolean).join(', ')}
              </p>
            )}

            {c.referredBy && (
              <p className="mt-1 text-[16px] text-ink-2">
                来源：由{' '}
                <button
                  onClick={() => navigate(`/customers/${c.referredBy!.id}`)}
                  className="font-medium text-brand-700 hover:underline"
                >
                  {c.referredBy.fullName}
                </button>{' '}
                介绍
              </p>
            )}
            {c.referrals.length > 0 && (
              <p className="mt-1 text-[16px] text-ink-2">
                已介绍 {c.referrals.length} 位顾客
              </p>
            )}
            {c.optOut && (
              <p className="mt-2">
                <Badge tone="neutral">已拒收自动提醒</Badge>
              </p>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="primary" icon={<Plus size={20} />} onClick={() => setNewCaseOpen(true)}>
              开单
            </Button>
            <Button icon={<Pencil size={18} />} onClick={() => setEditOpen(true)}>
              编辑
            </Button>
            {isOwner && (
              <Button
                variant="ghost"
                icon={<Trash2 size={18} />}
                aria-label="删除这位顾客"
                className="text-ink-3 hover:text-bad"
                onClick={() => {
                  setDelReason('')
                  setDelOpen(true)
                }}
              >
                删除
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 border-t border-line pt-4 text-[17px]">
          <Stat label="累计消费" value={isOwner ? rm(c.lifetimeAmount) : '—'} />
          <Stat
            label="当前欠款"
            value={isOwner ? rm(c.outstandingAmount) : c.hasOutstanding ? '有欠款' : '无'}
            tone={c.hasOutstanding ? 'warn' : undefined}
          />
          <Stat label="工单" value={`${c.caseCount} 张`} />
        </div>
      </Card>

      {c.healthNotes && (
        <p className="mb-4 flex gap-2 rounded-md border-l-[3px] border-info bg-info-bg px-4 py-3 text-[17px] text-info">
          <Info size={20} className="mt-0.5 shrink-0" />
          <span>
            <span className="font-semibold">健康备注：</span>
            {c.healthNotes}
          </span>
        </p>
      )}

      <Card title={`工单 (${c.cases.length})`} bodyClassName="p-0! sm:p-0!">
        {c.cases.length === 0 ? (
          <EmptyState
            title="这位顾客还没有工单"
            action={
              <Button variant="primary" size="lg" icon={<Plus size={20} />} onClick={() => setNewCaseOpen(true)}>
                为他开单
              </Button>
            }
          />
        ) : (
          <ul>
            {c.cases.map((cs) => (
              <li key={cs.id}>
                <button
                  onClick={() => navigate(`/cases/${cs.id}`)}
                  className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 border-b border-line px-5 py-4 text-left transition-colors last:border-0 hover:bg-sunken sm:px-6"
                >
                  <span className="tnum w-24 shrink-0 text-[17px] font-semibold">{cs.caseNo}</span>
                  <span className="min-w-40 flex-1 text-[17px]">
                    {cs.typeLabel}
                    {cs.arch ? ` ${ARCH_ZH[cs.arch]}` : ''}
                    {cs.currentStep && (
                      <span className="block text-[15px] text-ink-3">{cs.currentStep}</span>
                    )}
                  </span>
                  <Badge tone={caseStatusTone(cs.status)}>{CASE_STATUS_ZH[cs.status]}</Badge>
                  <span className="tnum w-28 shrink-0 text-[15px] text-ink-2">
                    {cs.deliveredAt ? (
                      <>
                        交付 {fmtDate(cs.deliveredAt)}
                        <span className="block text-ink-3">
                          {relativeDay(cs.deliveredAt)}
                        </span>
                      </>
                    ) : cs.promisedDate ? (
                      `交期 ${fmtDate(cs.promisedDate)}`
                    ) : null}
                  </span>
                  <span className="tnum w-24 shrink-0 text-right text-[17px]">
                    {rm(cs.finalAmount)}
                    {cs.balanceAmount > 0 && (
                      <span className="block text-[15px] font-semibold text-warn">
                        欠 {rm(cs.balanceAmount)}
                      </span>
                    )}
                  </span>
                  {/* 复购线索：满 4 年提示该换 */}
                  {cs.dueForRenewal && (
                    <Badge tone="warn" icon={<AlertCircle size={14} />}>
                      该换了
                    </Badge>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <NewCaseDrawer
        open={newCaseOpen}
        onClose={() => setNewCaseOpen(false)}
        presetCustomer={{ id: c.id, fullName: c.fullName }}
      />

      <CustomerDrawer open={editOpen} onClose={() => setEditOpen(false)} customerId={c.id} />

      <Modal
        open={delOpen}
        onClose={() => setDelOpen(false)}
        title={`删除顾客 ${c.fullName}？`}
        footer={
          <>
            <Button size="lg" onClick={() => setDelOpen(false)}>
              取消
            </Button>
            <Button
              size="lg"
              variant="danger"
              disabled={delReason.trim().length < 4}
              loading={del.isPending}
              onClick={() => del.mutate()}
            >
              确认删除
            </Button>
          </>
        }
      >
        {blockingCases > 0 ? (
          <p className="mb-3 rounded-md border border-warn-line bg-warn-bg px-4 py-3 text-[16px] text-warn">
            这位顾客还有 {blockingCases} 张进行中或未结清的工单，删不掉。
            要先把那些工单处理完或删掉。
          </p>
        ) : (
          <p className="mb-3">
            顾客会从搜索和列表里消失。为了账目能对得上，历史收款记录仍会保留在财务汇总里，
            但不再显示姓名。
          </p>
        )}
        <input
          autoFocus
          value={delReason}
          onChange={(e) => setDelReason(e.target.value)}
          placeholder="删除原因（至少 4 个字，例：重复建档）"
          className="h-12 w-full rounded-md border border-line-strong px-3.5 text-[17px] focus:border-brand-600 focus:outline-none"
        />
        <p className="mt-2 text-[15px] text-ink-3">删除记录会留在审计日志里。</p>
      </Modal>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'warn' }) {
  return (
    <span>
      <span className="block text-[15px] text-ink-3">{label}</span>
      <span
        className={
          'tnum block text-[21px] font-bold ' + (tone === 'warn' ? 'text-warn' : '')
        }
      >
        {value}
      </span>
    </span>
  )
}
